/* eslint-disable no-empty-pattern */
import { ThemeProvider } from "@mui/material";

import React, { useContext, useEffect, Suspense, useState, useCallback } from "react";
import lazyWithRetry from "./utils/lazyWithRetry";

import ReactGA from "react-ga4";
import { HelmetProvider } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { ScreenTypes } from "./enums";

import "./App.scss";
import styles from "./app.module.scss";
import Header from "./component/home&Landingpage/mainPageApp/header/Header";
import { ROUTES } from "./constant";
import { muiTheme } from "./theme/muiTheme";
import { DialogSelected, ENVIRONMENT_TYPE } from "./constant/common";
import { MOBILE_MEDIA_QUERY } from "./constant/breakpoints";
import {
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  GET_CONFIG_ERROR,
  environment,
  IS_CORS_ERROR,
} from "./constant/environment";
import "./constant/environment";
import { useMapsLoader } from "./component/reusableComponent/map/mapsLoader";
import { Messages } from "./constant/messages/toastMessages";
import { StateContext } from "./context/globalContext/context";
import { iGlobalContext } from "./context/globalContext/interface";
import "./root.css";
import AppRoutes from "./routes/AppRoutes";
import BottomNavBar from "./component/reusableComponent/bottomNavBar/BottomNavBar";
import { prefetchBookingChunk } from "./utils/prefetch/prefetchBookingChunk";
import { prefetchChatChunk } from "./utils/prefetch/prefetchChatChunk";
import { shouldShowBottomNav } from "./component/reusableComponent/bottomNavBar/shouldShowBottomNav";
import { useSoftKeyboardClass } from "./hooks/useSoftKeyboardClass";
import useDisableZoom from "./hooks/useDisableZoom";
import MaintenancePage from "./component/home&Landingpage/mainPageApp/maintenancePage";
import Navbar from "./component/reusableComponent/navbar/navbar";
import ErrorPage from "./component/home&Landingpage/mainPageApp/ErrorPage/ErrorPage";
import { useThemeColorByPathname } from "./utils/useThemeColorByPathname";
import { applyBrandingToDocument, currentBrandingCode, resolvePartnerBranding } from "./utils/partnerBranding";
import Discount from "./component/reusableComponent/discount/discount";
import { usePixelEvent } from "./hooks/usePixelEvent";
import { setCurrentUserEmail } from "./constant/google-analytics/GAEvents";

// Imported Hooks
import { useAppPromoCode } from "./component/home&Landingpage/mainPageApp/hooks/useAppPromoCode";
import { useAppUtmParams } from "./component/home&Landingpage/mainPageApp/hooks/useAppUtmParams";
import { useAppInitialization } from "./component/home&Landingpage/mainPageApp/hooks/useAppInitialization";
import { useAppNavigationLogic } from "./component/home&Landingpage/mainPageApp/hooks/useAppNavigationLogic";
import { useBookingUrlSync } from "./component/home&Landingpage/mainPageApp/hooks/useBookingUrlSync";
import { useGlobalEvents } from "./component/home&Landingpage/mainPageApp/hooks/useGlobalEvents";
import { useHistorySync } from "./component/home&Landingpage/mainPageApp/hooks/useHistorySync";
import { useProductionSecurity } from "./component/home&Landingpage/mainPageApp/hooks/useProductionSecurity";
import { isPortraitLockActive } from "./utils/portraitLock";
import { isEmbedMode } from "./utils/embedMode";
import { getPortalSurface, isAppEmbedFlow, showsEmbedAppNav } from "./utils/embedPortalBridge";
import { portalNavigate } from "./utils/embedRouteRewrite";
import { BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT } from "./constant/bookingInProgressConstant";
import { moveToFlight } from "./utils/booking/breadCrumb/breadCrumbUtils";
import { getItemFromLocalStorage } from "./hooks/localStorage";
import { useAutoSaveItinerary } from "./hooks/useAutoSaveItinerary";
import { useBookingSessionExpiredToast } from "./hooks/useBookingSessionExpiredToast";
import { usePostLoginRedirect } from "./hooks/usePostLoginRedirect";
import { useSharedCartBootstrap } from "./hooks/useSharedCartBootstrap";
import { useBrowserLifecycleTracking } from "./services/intent/useBrowserLifecycleTracking";
import { useDeadClickTracking } from "./services/intent/useDeadClickTracking";
import { useFlushPendingTheresias } from "./services/intent/useFlushPendingTheresias";
import { useScrollDepthTracking } from "./services/intent/useScrollDepthTracking";
import SharedCartReviewSkeleton from "./component/BookingInProgress/component/sharedCartReviewSkeleton/SharedCartReviewSkeleton";
import LoadingDots, { SHOW_LOADING_DOTS } from "./component/reusableComponent/loadingDots/LoadingDots";
import { isResultDetailOnMobileRoute } from "./component/reusableComponent/resultDetailScreen/resultDetailSurface";
import EmbedSessionNotice from "./component/reusableComponent/embedSessionNotice/EmbedSessionNotice";

// The global `isMobile` is seeded `true` (Safari refresh fix, b394af8d2) and
// only re-measured by useGlobalEvents after mount — React runs the first
// render's effects before that lands, so on a desktop refresh they still see
// `true`. The two mobile↔desktop redirects below confirm with the viewport
// itself (same breakpoint as useGlobalEvents) before sending a user to the
// mobile-only /booking route.
const isMobileViewport = (): boolean => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

// Conditional / user-gated modals — lazy-loaded to keep the main bundle lean
// for the onboarding LCP path. None of these render on first paint.
const ZohoChatFullScreen = lazyWithRetry(
  () => import("./component/home&Landingpage/mainPageApp/ZohoChatFullScreen/ZohoChatFullScreen")
);
const Dialog = lazyWithRetry(() => import("./component/home&Landingpage/mainPageApp/dialog/Dialog"));
const PropertyDetailsModal = lazyWithRetry(
  () => import("./component/home&Landingpage/mainPageApp/propertyDetailsModal/propertyDetailsModal")
);
const ReviewsModal = lazyWithRetry(() => import("./component/home&Landingpage/mainPageApp/reviewsModal/reviewsModal"));
const BookingModals = lazyWithRetry(() => import("./component/home&Landingpage/mainPageApp/bookingModal/BookingModals"));
const SelectRoomModal = lazyWithRetry(() => import("./component/home&Landingpage/mainPageApp/selectRoomModal/selectRoomModal"));
const ResultDetailScreen = lazyWithRetry(() => import("./component/reusableComponent/resultDetailScreen/ResultDetailScreen"));
const WhoComingWithYouDialog = lazyWithRetry(
  () => import("./component/onBoarding&Preferences/onBoarding/components/whoComingWithYou/WhoComingWithYouDialog")
);

// localStorage key that marks "this user has seen the initial loading overlay at least once on this device". Persists across tabs and across in-app navigations. Cleared when the last open tab closes (graceful close hook + stale-heartbeat fallback for crashes) so a fresh "open the app again later" session re-shows the overlay.
const OVERLAY_SEEN_STORAGE_KEY = "_zenvoya_initial_loading_seen";
const SESSION_OVERLAY_SEEN_KEY = "_zenvoya_loading_seen_this_tab";

const OPEN_TABS_STORAGE_KEY = "_zenvoya_open_tabs";
const HEARTBEAT_INTERVAL_MS = 56000;

const HEARTBEAT_STALE_MS = 1624000;

type OpenTabEntry = { id: string; lastSeen: number };

// Reads the open-tabs list, filtering out entries whose heartbeat is older than HEARTBEAT_STALE_MS. Returns only "live" tabs from the current snapshot.
function readLiveOpenTabs(): OpenTabEntry[] {
  try {
    const raw = localStorage.getItem(OPEN_TABS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter(
      (t): t is OpenTabEntry =>
        t && typeof t.id === "string" && typeof t.lastSeen === "number" && now - t.lastSeen < HEARTBEAT_STALE_MS
    );
  } catch {
    return [];
  }
}

function writeOpenTabs(tabs: OpenTabEntry[]): void {
  try {
    localStorage.setItem(OPEN_TABS_STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // localStorage blocked — overlay falls back to single-tab in-session logic.
  }
}

// `crypto.randomUUID()` is the modern API but missing in pre-2021 browsers / non-secure contexts. Fallback to a timestamp+random string of comparable uniqueness — collision risk is negligible for a tabs-list with a handful of entries.
function generateTabId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const MAPS_START_MAX_DELAY_MS = 5000;
const MAPS_START_EVENTS = ["pointerdown", "keydown", "focusin"] as const;

// Mounted by App once Maps should start loading. useMapsLoader pins the key for
// the page, shared with AppMap and the hotel detail. Mirrors the old
// <LoadScript onLoad={...}>: flips `scriptLoaded` in context once Maps is ready.
function MapsScriptLoader() {
  const { setScriptLoaded }: iGlobalContext = useContext(StateContext);
  const { isLoaded } = useMapsLoader();
  useEffect(() => {
    if (isLoaded) setScriptLoaded?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);
  return null;
}

function App() {
  useDisableZoom();

  // Google Maps JS (~300 KB with places/marker) is not needed for the first
  // paint, so it no longer loads at boot, where it competed with main.js on a
  // cold first visit. It starts after the page's load event, on the first user
  // interaction, or after MAPS_START_MAX_DELAY_MS — whichever comes first.
  // Autocomplete fields wait for `scriptLoaded`; AppMap and the hotel detail
  // call useMapsLoader themselves, so a page with a map still loads it at once.
  const [startMaps, setStartMaps] = useState(false);
  useEffect(() => {
    if (startMaps) return undefined;
    const start = () => setStartMaps(true);
    const timer = window.setTimeout(start, MAPS_START_MAX_DELAY_MS);
    const onLoad = () => {
      const w = window as any;
      if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(start, { timeout: 1000 });
      else start();
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    MAPS_START_EVENTS.forEach((type) => window.addEventListener(type, start, { once: true, passive: true }));
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("load", onLoad);
      MAPS_START_EVENTS.forEach((type) => window.removeEventListener(type, start));
    };
  }, [startMaps]);

  const state = useContext(StateContext);
  const {
    loginCreateAccountState,
    accessJWT,
    showZoho,
    isLazyLoaded,
    promoCode,
    showSelectRoomModal,
    setShowSelectRoomModal,
    showReviewsModal,
    setShowReviews,
    hotelDetailsPageState: hotelDetails,
    showPropertyDetails,
    setShowPropertyDetails,
    openResultDetail,
    setOpenResultDetail,
    activeBreadCrumbs,
    isMobile,
    isBottomDrawerOpen,
    isBookingSearchForm,
    userInfo,
    setShowWhoComingWithYouModal,
    isActivityReviewPage,
    setIsLazyLoaded,
    screen,
    setScreen,
    firstScreen,
    selectedFlightOptions,
    sharedCartLoading,
    bookingDetails,
    setBookingDetails,
    fareId,
    setFareId,
    setBreadList,
    setActiveBreadCrumbs,
    branding,
    setBranding,
  }: iGlobalContext = state;

  useThemeColorByPathname();
  useAutoSaveItinerary();
  usePostLoginRedirect();
  useSharedCartBootstrap();
  // Theresias browser-native telemetry — mounted once at App root.
  // Lifecycle: tab-leave / tab-return / tab-close / browser-close.
  // Scroll-depth: 25 / 50 / 75 / 100 percent buckets, once per route.
  // Both hooks register their OWN listeners and do NOT touch the existing
  // pagehide / visibilitychange / scroll handlers elsewhere in App.tsx or
  // useGlobalEvents.ts — browsers deliver events to all listeners
  // independently.
  useBrowserLifecycleTracking();
  useScrollDepthTracking();
  // Dead-click telemetry — capture-phase document listener that flags clicks
  // on interactive-looking elements yielding no response within 800 ms. Per-
  // view dedupe + 1.5 s rage debounce + session cap (20) keep volume bounded.
  // Defensively try/catched inside the handler so a tracking bug can never
  // break user clicks. See src/services/intent/useDeadClickTracking.ts.
  useDeadClickTracking();
  // Pre-login buffer flusher. The intent transport (postIntentEvent.ts)
  // silently buffers events to localStorage when accessJWT or userInfo.id
  // is missing — i.e. the entire onboarding funnel pre-signup. This hook
  // watches both auth fields and drains the queue (sequentially, preserving
  // funnel order) once login completes. See useFlushPendingTheresias.ts.
  useFlushPendingTheresias();

  useEffect(() => {
    setIsLazyLoaded(true);
    const el = document.getElementById("zv-boot-loader");
    if (!el) return;
    // Logged-in user landed on /onboarding directly (typed URL, stale tab, shared link): useOnBoarding's mount effect navigates them to Home and OnBoarding renders null in the meantime. Keep the boot loader visible through the redirect so the user sees a continuous fox loader instead of a flash. Removal is handled by the pathname-watcher useEffect below once the redirect lands.
    if (window.location.pathname.startsWith(ROUTES.onBoarding) && userInfo?.loggedIn) return;
    requestAnimationFrame(() => {
      el.style.transition = "opacity 180ms ease";
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      setTimeout(() => el.remove(), 220);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the GA/Pixel email cache in sync with StateContext.userInfo. Fixes
  // `Failed uploading to Mailchimp: Missing email in payload` for events like
  // booking-form-flights-search-click — localStorage's encrypted user_info blob
  // can be stale/empty at event fire-time, but context is always current.
  useEffect(() => {
    setCurrentUserEmail(userInfo?.email || "");
  }, [userInfo?.email]);

  useEffect(() => {
    if (userInfo?.loggedIn) {
      prefetchBookingChunk();
      prefetchChatChunk();
    }
  }, [userInfo?.loggedIn]);

  useEffect(() => {
    applyBrandingToDocument(branding);
  }, [branding]);

  useEffect(() => {
    const code = currentBrandingCode();
    if (!code) return;
    resolvePartnerBranding(code, accessJWT)
      .then(setBranding)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll when login modal is open to prevent background shifting on iOS.
  // Skip for SELECT_FARE — that modal has its own scroll lock in selectFareDialog.tsx
  // which preserves the scroll position via `top: -${scrollY}px`. The App-level lock
  // here doesn't preserve scroll, so on mobile it visibly snaps the background to the
  // top when the fare modal opens.
  useEffect(() => {
    const isSelectFareModal =
      loginCreateAccountState.flag &&
      loginCreateAccountState.page === DialogSelected.SELECT_FARE;
    if (loginCreateAccountState.flag && !isSelectFareModal) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.touchAction = "";
    };
  }, [loginCreateAccountState.flag, loginCreateAccountState.page]);

  const { sendPixelEvent } = usePixelEvent();

  const { getPromoCode, updatePromoCode } = useAppPromoCode();
  useAppUtmParams(getPromoCode);
  const {} = useBookingUrlSync();
  const { getSingleHistoryDetail } = useHistorySync();
  const { networkError, isVersionMatching, handleRefresh, loading: isAppInitLoading } = useAppInitialization();
  // One-shot toast when a dead booking session was reset on boot (see
  // sanitizeStaleBookingSession) — user was dropped back to Home.
  useBookingSessionExpiredToast();

  // Loading-overlay gate — combines three signals:
  // 1. In-session toggle (`showLoadingOverlay`): flips false at first dismiss so the overlay doesn't reappear on later in-app navigations.
  // 2. Cross-tab / cross-session "seen" flag (localStorage): once the user has seen one full cycle on any device tab, never show again. Lazy- read on mount so the check is one-shot, not per-render. Storage event listener below propagates dismiss between simultaneously-open tabs.
  // 3. Route + auth gate (in JSX): only show on `/` or `/onboarding`, and only for logged-in users (logged-out users see signup, not a loading state).
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [hasSeenOverlay, setHasSeenOverlay] = useState(() => {

    try {
      if (sessionStorage.getItem(SESSION_OVERLAY_SEEN_KEY) === "true") {
        // Same-tab reload restores localStorage too — keeps peer-tab gating consistent in case the previous instance's localStorage somehow got wiped (older handleClose, user testing).
        try {
          localStorage.setItem(OVERLAY_SEEN_STORAGE_KEY, "true");
        } catch {}
        return true;
      }
    } catch {
      // sessionStorage blocked — fall through to other checks.
    }

    const liveTabs = readLiveOpenTabs();
    if (liveTabs.length === 0) {
      try {
        localStorage.removeItem(OVERLAY_SEEN_STORAGE_KEY);
      } catch {
        // localStorage blocked — no harm, in-session state still works.
      }
      return false;
    }

    let localSeen = false;
    try {
      localSeen = localStorage.getItem(OVERLAY_SEEN_STORAGE_KEY) === "true";
    } catch {
      // localStorage blocked (private mode, storage disabled). Default to "not seen" so the in-session overlay still works on this tab.
    }
    if (localSeen) {
      try {
        sessionStorage.setItem(SESSION_OVERLAY_SEEN_KEY, "true");
      } catch {
        // sessionStorage blocked — best-effort.
      }
    }
    return localSeen;
  });


  const [homeContentReady, setHomeContentReady] = useState(
    typeof window !== "undefined" && (window as Window & { __zvHomeContentReady?: boolean }).__zvHomeContentReady === true
  );

  useEffect(() => {
    if (homeContentReady) return;

    if ((window as Window & { __zvHomeContentReady?: boolean }).__zvHomeContentReady === true) {
      setHomeContentReady(true);
      return;
    }
    const handler = () => setHomeContentReady(true);
    window.addEventListener("zv-home-content-ready", handler);
    return () => window.removeEventListener("zv-home-content-ready", handler);
  }, [homeContentReady]);

  const handleLoadingDismiss = useCallback(() => {
    setShowLoadingOverlay(false);
    try {
      localStorage.setItem(OVERLAY_SEEN_STORAGE_KEY, "true");
    } catch {
      // localStorage blocked — in-session state already prevents re-show on this tab; cross-tab/session persistence is the casualty.
    }
    try {
      sessionStorage.setItem(SESSION_OVERLAY_SEEN_KEY, "true");
    } catch {
      // sessionStorage blocked — same-tab reload suppression won't work, but in-session state still does.
    }
  }, []);

  // Cross-tab sync: if another tab dismisses first, the `storage` event fires here. Mark seen + hide so the overlay doesn't double-show in parallel tabs. `storage` event does NOT fire in the tab that set the value, so this only affects OTHER tabs — correct one-way sync direction.
  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === OVERLAY_SEEN_STORAGE_KEY && e.newValue === "true") {
        setHasSeenOverlay(true);
        setShowLoadingOverlay(false);

        try {
          sessionStorage.setItem(SESSION_OVERLAY_SEEN_KEY, "true");
        } catch {
          // sessionStorage blocked — best-effort.
        }
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  // Tab registration + heartbeat. Keeps a list of live tabs in localStorage so the seen-flag can be reset only when the LAST open tab closes (graceful close hook + 90s stale heartbeat fallback for crashed tabs). Multiple tabs open in parallel → flag persists. Last tab closes → flag wiped → next session shows overlay fresh.
  useEffect(() => {
    const tabId = generateTabId();

    // Register self in the open-tabs list.
    writeOpenTabs([...readLiveOpenTabs(), { id: tabId, lastSeen: Date.now() }]);

    // Heartbeat — refresh own `lastSeen` every HEARTBEAT_INTERVAL_MS so other tabs (and future fresh-session checks) see this tab as alive. Also implicitly prunes stale entries (readLiveOpenTabs filters them).
    const heartbeatId = window.setInterval(() => {
      const others = readLiveOpenTabs().filter((t) => t.id !== tabId);
      writeOpenTabs([...others, { id: tabId, lastSeen: Date.now() }]);
    }, HEARTBEAT_INTERVAL_MS);


    const handleClose = () => {
      const remaining = readLiveOpenTabs().filter((t) => t.id !== tabId);
      writeOpenTabs(remaining);
      if (remaining.length === 0) {
        try {
          localStorage.removeItem(OVERLAY_SEEN_STORAGE_KEY);
        } catch {
          // localStorage blocked — flag lingers; sessionStorage of any surviving tab still suppresses re-show on its reload.
        }
      }
    };

    window.addEventListener("pagehide", handleClose);
    window.addEventListener("beforeunload", handleClose);

    return () => {
      clearInterval(heartbeatId);
      handleClose();
      window.removeEventListener("pagehide", handleClose);
      window.removeEventListener("beforeunload", handleClose);
    };
  }, []);
  useAppNavigationLogic(updatePromoCode, getSingleHistoryDetail);
  useProductionSecurity();
  const {} = useGlobalEvents();
  const location = useLocation();
  const { pathname } = location;

  const showBottomNav = shouldShowBottomNav({
    pathname,
    isMobile,
    isBottomDrawerOpen,
    isBookingSearchForm,
  });
  // Hides the fixed bottom nav while the mobile keyboard is up (see hook).
  useSoftKeyboardClass(showBottomNav);

  const showEmbedNavbar = showsEmbedAppNav(pathname);

  const hideSideBar =
    ((activeBreadCrumbs?.value === BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT?.ReviewNew || isActivityReviewPage) &&
      !isMobile) ||
    //
    (isEmbedMode() && !showEmbedNavbar);

  const navigate = useNavigate();

  useEffect(() => {
    const overlayWouldRender =
      SHOW_LOADING_DOTS &&
      showLoadingOverlay &&
      !hasSeenOverlay &&
      userInfo?.loggedIn &&
      (pathname === ROUTES.Home || pathname.startsWith(ROUTES.onBoarding));
    if (overlayWouldRender) {
      try {
        localStorage.setItem(OVERLAY_SEEN_STORAGE_KEY, "true");
      } catch {
        // localStorage blocked — sessionStorage still gives same-tab reload suppression.
      }
      try {
        sessionStorage.setItem(SESSION_OVERLAY_SEEN_KEY, "true");
      } catch {
        // sessionStorage blocked — best-effort; in-session state remains.
      }
    }
  }, [showLoadingOverlay, hasSeenOverlay, userInfo?.loggedIn, pathname]);

  // Pair to the deferred boot-loader case in the mount effect above. When the
  // /onboarding → Home redirect lands (pathname leaves /onboarding), fade out
  // the boot loader normally. No-op on every other route change because the
  // element is gone after first removal.
  useEffect(() => {
    if (pathname.startsWith(ROUTES.onBoarding)) return;
    const el = document.getElementById("zv-boot-loader");
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.transition = "opacity 180ms ease";
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      setTimeout(() => el.remove(), 220);
    });
  }, [pathname]);

  // Mirror of the redirect in BookingInProgress.tsx. When the viewport shrinks
  // to mobile while the user is viewing the inline booking drawer on Home
  // (screen/firstScreen === newBooking), send them to /booking so the listing
  // is preserved in the mobile full-page layout instead of reverting to the
  // empty Home screen.
  //
  // Skip while the portrait-lock overlay is up — the user can't see the app and
  // any navigation behind the overlay (combined with BookingInProgress.tsx's
  // counterpart bridge) would flip-flop their route + screen state by the time
  // they rotate back to portrait. See `utils/portraitLock.ts`.
  useEffect(() => {
    const root = document.documentElement;
    //
    if (isAppEmbedFlow()) root.setAttribute("data-zv-app-embed", "1");
    else root.removeAttribute("data-zv-app-embed");

    if (showEmbedNavbar && isMobile) root.setAttribute("data-zv-embed-nav", "1");
    else root.removeAttribute("data-zv-embed-nav");
  }, [showEmbedNavbar, isMobile, userInfo?.loggedIn, userInfo?.id, accessJWT]);

  useEffect(() => {
    if (isPortraitLockActive()) return;
    if (isEmbedMode()) return;
    if (
      isMobile &&
      pathname === ROUTES.Home &&
      (screen === ScreenTypes.newBooking || firstScreen === ScreenTypes.newBooking)
    ) {
      // Desktop refresh on the Shop screen: `isMobile` is still the seeded
      // `true` here. Navigating went Home → /booking (skeleton) → Home via
      // BookingInProgress's desktop bridge; land directly where that bridge
      // ended up — Home with screen=newBooking — instead.
      if (!isMobileViewport()) {
        if (screen !== ScreenTypes.newBooking) setScreen(ScreenTypes.newBooking);
        return;
      }
      navigate(ROUTES.BookingInProgress, { replace: true });
    }
  }, [isMobile, pathname, screen, firstScreen, navigate, setScreen]);

  useEffect(() => {
    if (!isMobile) return;
    setOpenResultDetail((prev) => (prev && !isResultDetailOnMobileRoute(prev, pathname) ? null : prev));
  }, [pathname, isMobile, setOpenResultDetail]);

  useEffect(() => {
    // Skip the layout flip while the portrait-lock overlay is up — the `isMobile`
    // branch decision would be made on a transient (and visually invisible) rotated
    // viewport and could leave the user on the wrong route after they rotate back.
    if (isPortraitLockActive()) return;
    if (pathname === ROUTES.PaymentSuccess || pathname === ROUTES.PaymentFailure) return;
    if (activeBreadCrumbs?.value !== BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.ReviewNew) return;
    if (!selectedFlightOptions?.flights) return;
    if (selectedFlightOptions?.hotels) return;
    //
    //
    if (isEmbedMode()) {
      // Native app on a phone: hand the chat flight off to the Shop surface like
      // chat-hotel already does (useSelectRoomHooks → navigateWithParams(/booking)
      // → /portal/shop?embed=1). The setScreen branch below only mounts the review
      // under /portal/chat; the mobile fare panel (Booking Options) closes on a
      // route change, so without one it stayed on top and Continue looked dead.
      // PortalSurface skips its Shop reset for a chat booking origin, so the picked
      // fare / ReviewNew crumb survive. portalNavigate no-ops once on /portal/shop.
      if (isAppEmbedFlow() && isMobile && isMobileViewport()) {
        portalNavigate(navigate, ROUTES.BookingInProgress, { replace: true });
        return;
      }
      if (getPortalSurface() !== null && screen !== ScreenTypes.newBooking) {
        setScreen(ScreenTypes.newBooking);
      }
      return;
    }
    // isMobileViewport(): a desktop refresh on the flight review must not take
    // the mobile branch on the seeded `isMobile` (see the helper above).
    if (isMobile && isMobileViewport()) {
      if (pathname === ROUTES.BookingInProgress) return;
      navigate(ROUTES.BookingInProgress, { replace: true });
    } else {
      if (screen === ScreenTypes.newBooking) return;
      setScreen(ScreenTypes.newBooking);
    }
  }, [
    isMobile,
    screen,
    activeBreadCrumbs?.value,
    selectedFlightOptions?.flights,
    selectedFlightOptions?.hotels,
    setScreen,
    pathname,
    navigate,
  ]);

  // Recover an orphaned flight selection on page load. Picking a fare writes
  // `fareId` (localStorage) + an optimistic `selectedFlightId` (sessionStorage)
  // SYNCHRONOUSLY, but the availability check that confirms the pick (and sets
  // `isFlightAvailable: true`) runs async and CANNOT survive a page reload. So
  // refreshing mid-check orphans the selection: the chat flight card shows
  // selected (its checkmark = `selectedFlightId === uniqueId && isFlightAvailable`)
  // / the "Review & Select Fare" card stays selected + its re-select button is
  // disabled (`selectedFareId === id`, i.e. fareId), yet the booking can never
  // proceed because availability never confirmed — the user is stuck, unable to
  // move forward or pick again.
  //
  // On a fresh load there is by definition NO availability request in flight, so
  // a set fare/flight with `isFlightAvailable !== true` is always orphaned →
  // clear it so the card reverts to unselected and is pickable again. A
  // genuinely-confirmed selection (`isFlightAvailable === true`) is preserved, so
  // reloading a completed selection is unaffected. Runs once per page load
  // (empty deps capture the persisted-at-mount values, which is exactly intended).
  useEffect(() => {
    const hasIncompleteFlightPick =
      (!!fareId || !!bookingDetails?.selectedFlightId) && bookingDetails?.isFlightAvailable !== true;
    if (!hasIncompleteFlightPick) return;
    setFareId("");
    setBookingDetails((prev: any) => ({
      ...prev,
      selectedFlightId: "",
      selectedFlightDetails: null,
      isFlightAvailable: false,
    }));
    // If the breadcrumb had already advanced to the review step on the orphaned
    // pick, roll it back to the flight step so the chat-flight watcher above
    // doesn't strand the user on an empty review (the flight section is gated on
    // `selectedFlightId !== ""`, which we just cleared).
    if (activeBreadCrumbs?.value === BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.ReviewNew) {
      moveToFlight(setBreadList, setActiveBreadCrumbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ReactGA.isInitialized) {
    ReactGA.initialize(GOOGLE_ANALYTICS_MEASUREMENT_ID);
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    // Delay sending appLoaded event to allow GA to set the _ga cookie
    setTimeout(() => {
      sendPixelEvent("appLoaded", { event: "app_loaded" });
    }, 500);
  }

  // Lazy-load disable-devtool so the library stays out of the main bundle.
  useEffect(() => {
    if (environment !== ENVIRONMENT_TYPE.PRODUCTION) return;
    import("disable-devtool").then(({ default: DisableDevtool }) => {
      DisableDevtool({
        ondevtoolopen: () => {
          debugger;
        },
      });
    });
  }, []);

  useEffect(() => {
    if (userInfo?.loggedIn) {
      const hasSeen = getItemFromLocalStorage("hasSeenTravelerDialog");
      const tempTravelWith = getItemFromLocalStorage("temp_onboarding_travelWith");

      if (!hasSeen && (tempTravelWith === "Friends" || tempTravelWith === "Family" || tempTravelWith === "Partners")) {
        setShowWhoComingWithYouModal(true);
      }
    }
  }, [userInfo?.loggedIn, setShowWhoComingWithYouModal]);

  const weatherAdvisoryRoutes = [ROUTES.weatherAdvisory, ROUTES.FlightDelay, ROUTES.FlightSelect];
  const isWeatherAdvisory = weatherAdvisoryRoutes.includes(location.pathname);

  // Shared-cart link is loading → show the review-page skeleton until the
  // snapshot is reconstructed into the cart (the bootstrap hook keeps running
  // since it's declared above this return).
  if (sharedCartLoading) return <SharedCartReviewSkeleton />;

  if (!isVersionMatching)
    return (
      <ErrorPage
        onRefresh={handleRefresh}
        message={
          Messages?.["errorPage.incompatible.version"]?.messageTitle +
          Messages?.["errorPage.incompatible.version"]?.message
        }
      />
    );

  if (networkError)
    return (
      <ErrorPage
        onRefresh={handleRefresh}
        message={Messages?.["errorPage.networkError"]?.messageTitle + Messages?.["errorPage.networkError"]?.message}
      />
    );

  if (GET_CONFIG_ERROR || IS_CORS_ERROR) {
    // IS_CORS_ERROR/GET_CONFIG_ERROR are latched at module load and only clear
    // on a full reload (which re-runs the config fetch). A client-side nav or
    // the login modal can't escape this branch, so the header actions must hard
    // reload to Home — once the backend is back the app re-inits and the user
    // lands on a working login/home.
    const reloadToHome = () => window.location.assign(ROUTES.Home);
    return (
      <div>
        <Navbar onLogoClick={reloadToHome} onLoginClick={reloadToHome} />
        <MaintenancePage isCorsError={IS_CORS_ERROR} />
      </div>
    );
  }

  // Previously: `if (loading) return <Loader />` blocked the entire app until
  // guest-token polling + fetchCountries resolved (up to 10s fallback). The
  // first paint is now not gated on these — they continue in the background
  // and the route-level UI renders immediately.

  return (
    <HelmetProvider>
      <ThemeProvider theme={muiTheme}>
        {
          // isMobile &&
          pathname !== ROUTES.TripDetails &&
            !pathname.startsWith(`${ROUTES.TripDetails}/`) &&
            !pathname.startsWith(ROUTES.TripDetailsOld) &&
            !pathname.startsWith(ROUTES.ItineraryDetails) &&
            !pathname.startsWith(ROUTES.MyTrips) &&
            !pathname.startsWith(ROUTES.SavedTrip) &&
            !pathname.startsWith(ROUTES.History) &&
            !pathname.startsWith(ROUTES.BookingInProgress) &&
            !pathname.startsWith(ROUTES.ExploreCity) &&
            !pathname.startsWith(ROUTES.TripHelp) &&
            !pathname.startsWith(ROUTES.FaqAssistance) &&
            !pathname.startsWith(ROUTES.onBoarding) &&
            !pathname.startsWith(ROUTES.preferences) &&
            !pathname.startsWith(ROUTES.Account) &&
            !pathname.startsWith(ROUTES.ModifyFlights) &&
            !pathname.startsWith(ROUTES.Tos) &&
            !pathname.startsWith(ROUTES.PrivacyPolicy) &&
            !pathname.startsWith(ROUTES.TicketDetails) &&
            !pathname.startsWith("/app") &&
            !pathname.startsWith("/shared") &&
            !pathname.startsWith("/payment") &&
            // /trips (lares): the global Header supplies the desktop chat-page
            // sidebar, but on mobile the lares page owns its own back+title
            // header (HeaderNavTitle), so suppress the global navbar there.
            !(isMobile && pathname.startsWith("/trips")) && (
              <div className={styles.headerContainer}>
                {isLazyLoaded && promoCode[0]?.description && (
                  <Discount promoCodeDescription={promoCode[0]?.description} />
                )}
                {isLazyLoaded && !hideSideBar && <Header />}
              </div>
            )
        }
        <Suspense fallback={null}>{showZoho && <ZohoChatFullScreen />}</Suspense>
        <div className={`${isWeatherAdvisory ? styles.hideContentContainer : styles.contentContainer}`}>
          <AppRoutes />
        </div>

        {showBottomNav && <BottomNavBar />}

        {startMaps && <MapsScriptLoader />}

        <EmbedSessionNotice />

        <Suspense fallback={null}>
          {/* Dialog for Log in, create account */}
          {loginCreateAccountState.flag && (
            <div className={styles.dialogMainContainer}>
              <div className={styles.overlay}></div>
              <Dialog />
            </div>
          )}
          {showSelectRoomModal && (
            <SelectRoomModal
              isOpen={showSelectRoomModal}
              onClose={() => {
                setShowSelectRoomModal(false);
              }}
            />
          )}
          {showReviewsModal && (
            <ReviewsModal
              isOpen={showReviewsModal}
              onClose={() => {
                setShowReviews(false);
              }}
            />
          )}
          {showPropertyDetails && (
            <PropertyDetailsModal
              propertyDetails={hotelDetails}
              isOpen={showPropertyDetails}
              onClose={() => setShowPropertyDetails(false)}
            />
          )}
          {openResultDetail && isMobile && (
            <ResultDetailScreen
              detail={openResultDetail}
              variant="overlay"
              onClose={() => setOpenResultDetail(null)}
            />
          )}
          <BookingModals />
          <WhoComingWithYouDialog />
        </Suspense>

        {/* Sibling overlay loading indicator. Renders on top of the home page
            during the post-fox-loader window where backend init (appConfig,
            countries, prompts) is still resolving. Gated to non-onboarding
            paths so the boot loader covers the onboarding/redirect window
            without overlap. SHOW_LOADING_DOTS in LoadingDots.tsx is the
            master toggle — flip to false to disable globally. */}
        {SHOW_LOADING_DOTS &&
          showLoadingOverlay &&
          !hasSeenOverlay &&
          userInfo?.loggedIn &&
          (pathname === ROUTES.Home || pathname.startsWith(ROUTES.onBoarding)) && (
            <LoadingDots isPageReady={!isAppInitLoading && homeContentReady} onDismiss={handleLoadingDismiss} />
          )}
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
